import { FeatureFlagName } from "../../tenant/features/featureFlags";

export interface AdminFeatureInfo {
	/** Feature name as it appears in the Admin portal's Feature Configuration. */
	title: string;
	/** What the page/section offers — shown in the title tooltip and the locked state. */
	description: string;
}

/**
 * Copy for every utility-admin feature flag, mirroring the Admin portal's Feature
 * Configuration. Keyed by flag so a page declares only its flag and the layout finds
 * the words. Utility-admin pages are not translated yet (no `utilityAdmin` namespace
 * consumer exists); when they are, this is the one place to move to i18n.
 */
export const ADMIN_FEATURE_INFO: Partial<Record<FeatureFlagName, AdminFeatureInfo>> = {
	// Bills Payment & Operations
	[FeatureFlagName.ADMIN_VENDING_HISTORY]: {
		title: "Vending History",
		description:
			"View and track all electricity vending transactions, including payment status, token details, and transaction history.",
	},
	[FeatureFlagName.ADMIN_POSTPAID_HISTORY]: {
		title: "Postpaid History",
		description:
			"View and track all postpaid payment records, including payment status and transaction history for postpaid customers.",
	},
	[FeatureFlagName.ADMIN_BULK_PURCHASES]: {
		title: "Bulk Purchases",
		description:
			"View and manage purchase logs for customers buying across multiple meters in a single transaction.",
	},
	[FeatureFlagName.ADMIN_CUSTOMERS]: {
		title: "Customers",
		description:
			"Get a complete 360° view of each customer, including their account details, meters, and transaction history in one place.",
	},
	[FeatureFlagName.ADMIN_BROADCAST]: {
		title: "Broadcast",
		description:
			"Send announcements to customers through push notifications, pop-ups, banners, and scrolling messages across the app.",
	},
	[FeatureFlagName.ADMIN_DISTRICT_REVENUE_ANALYTICS]: {
		title: "District Revenue Analytics",
		description:
			"View revenue contribution and trends broken down by district, to track performance across service areas.",
	},
	[FeatureFlagName.ADMIN_REFUNDS]: {
		title: "Refunds",
		description:
			"Track every automatic refund of a failed purchase, see why an attempt failed and retry refunds that need help.",
	},
	// Service Request
	[FeatureFlagName.ADMIN_REFERRAL_SYSTEM]: {
		title: "Referral System",
		description: "Manage staff referral codes and track customer sign-ups generated through referrals.",
	},
	[FeatureFlagName.ADMIN_AUDIT_LOG]: {
		title: "Audit Log",
		description:
			"View a record of activity across the platform, including the date, action taken, user, and IP address for each event.",
	},
	[FeatureFlagName.ADMIN_SERVICE_REQUEST_SETTINGS]: {
		title: "Settings",
		description:
			"Configure utility, team, department, and role-level settings for how service requests are managed.",
	},
	[FeatureFlagName.ADMIN_SERVICE_REQUEST_WITH_DEPARTMENT]: {
		title: "Service Request with Department",
		description:
			"Route service requests to the correct department automatically, so tickets reach the right team without manual reassignment.",
	},
	// Support Desk
	[FeatureFlagName.ADMIN_CREATE_SERVICE_REQUEST]: {
		title: "Create Service Request",
		description:
			"Log a service request on a customer's behalf, for cases where the customer contacts support directly rather than submitting one themselves.",
	},
	[FeatureFlagName.ADMIN_CUSTOMER_MANAGEMENT]: {
		title: "Customer Management",
		description: "Handle customer tickets end-to-end, from creation through resolution, in one place.",
	},
	// Roles Management
	[FeatureFlagName.ADMIN_CREATE_AND_ASSIGN_ROLES]: {
		title: "Create & Assign Roles",
		description:
			"Create new roles and assign them to Admin app users to control what each person can access and do.",
	},
	[FeatureFlagName.ADMIN_VIEW_PERMISSIONS]: {
		title: "View Permissions",
		description:
			"See the full list of permissions available on the platform and which roles they're assigned to.",
	},
	[FeatureFlagName.ADMIN_EDIT_PERMISSIONS]: {
		title: "Edit Permissions",
		description: "Modify what each role is allowed to do by adding or removing specific permissions.",
	},
	[FeatureFlagName.ADMIN_INTEGRATION_ACCESS_CONTROL]: {
		title: "Integration Access Control",
		description: "Manage which roles or users have access to integration-related settings and credentials.",
	},
};
